// Module 10 — the single time-uniform Fabric material for animated flow. The entire 60-FPS story
// rides on ONE material instance shared by every edge polyline and mutated in place each frame
// (setFlowTime), never a per-Entity CallbackProperty (the documented Cesium FPS cliff). The GLSL
// animates a moving stripe via fract(materialInput.s - time*speed); colours/encoding are 10.6's job.
import type { CesiumLike, MaterialLike } from "./cesium-api";
import type { CrisisBucket } from "./effects/spec";

export const FLOW_MATERIAL_TYPE = "PowerFlow";

// "Types of power grids" (v3 display fidelity): each voltage tier renders in its own hue, like the
// upstream Debug 3 map — the network hierarchy is readable at a glance. Display only.
// Okabe-Ito colour-blind-safe palette (Batch C 4.4): every voltage tier reads apart under deuteranopia/
// protanopia, and matches the on-screen legend + diagrams. Tier also carries a redundant non-colour cue
// (edge width by loading), so colour is reinforcement, never the sole channel. Injected as GLSL consts.
export const TIER_COLOR: Record<string, string> = {
  EHV: "#332288", // 500/230 kV bulk — blue-purple
  HV: "#E69F00", // 115–138 kV — orange
  ST: "#56B4E9", // 44–69 kV sub-transmission — sky-blue
  MV: "#009E73", // 20–30 kV distribution — teal
  LV: "#CC79A7", // <20 kV — rose
};

// P14 loading/danger RAMP: the same base/warn/hot/red `loadingPct` buckets the 10.6 encoder computes
// (`encodeCrisis` → `CrisisBucket`) now drive the EDGE colour too — an overloaded cable glows yellow→
// orange→red OVER its tier hue, while width stays the redundant non-colour cue. A bounded matrix of
// CACHED materials keyed `${tier}:${band}:${dir}` (lazily built in the adapter) keeps the FPS-cliff rule
// (one PolylineCollection + cached time-uniform materials mutated in place). At `base` the tier hue
// shows (the "types of power grids" channel); a danger band OVERRIDES it (CVD-safe Okabe-Ito danger ramp).
// warn/hot/red are SOLID danger hues that override the tier. `load` is NOT here — it's a per-tier BLEND
// (tier hue warmed toward LOAD_WARM) computed in bandRgbConst, so a loaded line keeps its voltage identity.
const BAND_COLOR: Record<"warn" | "hot" | "red", string> = {
  warn: "#F0E442", // ≥70% — yellow (brownout risk)
  hot: "#E69F00", // ≥85% — orange
  red: "#D55E00", // ≥100% — vermillion (overload)
};
const LOAD_WARM = "#F0C420"; // the warm amber the tier hue is blended toward at the `load` band (≥40%)
const LOAD_MIX = 0.5; // 50% tier hue + 50% warm — voltage still readable, but the line visibly "carries load"

// Pure threshold: loadingPct → band. MUST mirror `effects/faults.ts bucketOf` (the encoder's crisis
// channel) — both are trivially correct; this duplicate exists only so flow-material stays a leaf the
// adapter can use without importing the 10.6 encoder. The adapter prefers the encoded `spec.crisis`
// (the dead channel P14 wires live) and falls back to this when no effects encoder is injected.
export function bandOf(loadingPct: number): CrisisBucket {
  if (loadingPct >= 100) return "red";
  if (loadingPct >= 85) return "hot";
  if (loadingPct >= 70) return "warn";
  if (loadingPct >= 40) return "load"; // ≥40% — warm the tier hue so capacity reads on the secure grid
  return "base";
}

// A SINGLE mutable uniform (`time`) — the whole 60-FPS story rides on mutating it in place.
// Cesium's Fabric pipeline AUTO-DECLARES the `uniforms` keys (renamed per-instance, e.g. `time_2`)
// and rewrites references in `source`; re-declaring them here would collide (`<name>_N redefinition`).
// Likewise a uniform's GLSL type is INFERRED from its JS value, and a 4-number JS array infers as
// `mat2` (√4), not `vec4` — which broke `glowColor.rgb`. So the static colour + speed are GLSL
// consts inside the shader, leaving exactly one inferred `float time` uniform.
export interface FlowFabric {
  type: string;
  uniforms: { time: number };
  source: string;
}

// `tier` selects the voltage-class hue (TIER_COLOR); `dir` selects the CASCADE DIRECTION the
// repeating chevron arrows march in: +1 = along the polyline (u→v, the solver's flowMw>0 sense),
// −1 = against it (flowMw<0). `band` (P14) overrides the hue with the loading-danger ramp colour when
// the edge is overloaded. The adapter swaps each edge's material between the variants when its branch
// SIGN or loading BAND changes, so the on-screen arrows always match the engine's真 direction + heat.
// Each (tier, band, dir) pair gets a UNIQUE Fabric type — Cesium caches compiled shaders by type.
// The chevron: a V-shaped repeating wavefront (leading edge slanted by the cross-line coordinate
// |t−0.5|, so each pulse reads as an arrowhead pointing the way it moves). Constant base glow keeps
// the network visible between pulses. Colour is injected as GLSL consts (a 4-number JS uniform
// array infers as mat2, not vec4 — see header).
function hexToVec3(hex: string): string {
  const h = hex.replace("#", "");
  const r = (parseInt(h.slice(0, 2), 16) / 255).toFixed(4);
  const g = (parseInt(h.slice(2, 4), 16) / 255).toFixed(4);
  const b = (parseInt(h.slice(4, 6), 16) / 255).toFixed(4);
  return `vec3(${r}, ${g}, ${b})`;
}
function rgbConst(tier: string): string {
  return hexToVec3(TIER_COLOR[tier] ?? TIER_COLOR["MV"]!);
}
// Linear hex blend a→b by t∈[0,1] (used for the `load` band: tier hue warmed toward LOAD_WARM;
// exported for adapter.ts's Phase 1.3 island-hue glyph tint — same primitive, different blend target).
export function mixHex(a: string, b: string, t: number): string {
  const p = (h: string, i: number): number => parseInt(h.replace("#", "").slice(i, i + 2), 16);
  const ch = (i: number): string => Math.round(p(a, i) * (1 - t) + p(b, i) * t).toString(16).padStart(2, "0");
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}
// base → the tier hue (voltage channel); `load` → the tier hue WARMED toward amber (voltage still readable,
// but the line visibly carries load); warn/hot/red → the solid danger hue (overrides tier).
function bandRgbConst(tier: string, band: CrisisBucket): string {
  if (band === "base") return rgbConst(tier);
  if (band === "load") return hexToVec3(mixHex(TIER_COLOR[tier] ?? TIER_COLOR["MV"]!, LOAD_WARM, LOAD_MIX));
  return hexToVec3(BAND_COLOR[band]);
}
// P14 prominence: a danger band must READ as danger, not blend into the base palette. Non-base bands get a
// brighter constant glow + a higher alpha FLOOR (so the whole edge — not just the marching chevron — glows
// in its danger hue), escalating base<warn<hot<red. Base keeps the subtle network-hierarchy look.
function bandGlow(band: CrisisBucket): { emFloor: number; aFloor: number } {
  switch (band) {
    case "load": return { emFloor: 0.34, aFloor: 0.46 }; // ≥40% — clearly brighter than idle base, below warn
    case "warn": return { emFloor: 0.55, aFloor: 0.55 };
    case "hot": return { emFloor: 0.85, aFloor: 0.62 };
    case "red": return { emFloor: 1.15, aFloor: 0.70 };
    default: return { emFloor: 0.12, aFloor: 0.30 }; // base — the original subtle look
  }
}

export function flowFabric(time = 0, tier = "MV", dir: 1 | -1 = 1, band: CrisisBucket = "base"): FlowFabric {
  // BIG marching chevrons (engine-v2 flow redesign): few, bold ">>>" arrowheads that visibly travel
  // along the edge in the engine's flow direction. `dir` = sign(flowMw): +1 marches u→v, −1 v→u.
  // Lower spatial frequency (×3.4, was ×9) ⇒ larger, more recognizable arrows; a strong cross-line
  // slant builds the V; the band is bright while the gaps are dim so the arrows read as discrete
  // chevrons, not a stripe. Only edges the engine marks energized get this material (see adapter).
  const phase = dir === 1 ? "materialInput.s * 3.4 - time * 1.15" : "materialInput.s * -3.4 - time * 1.15";
  const { emFloor, aFloor } = bandGlow(band); // P14: danger bands glow brighter + at a higher alpha floor
  return {
    type: `${FLOW_MATERIAL_TYPE}_${tier}_${dir === 1 ? "F" : "R"}_${band}`,
    uniforms: { time },
    source: [
      "czm_material czm_getMaterial(czm_materialInput materialInput) {",
      "  czm_material m = czm_getDefaultMaterial(materialInput);",
      `  const vec3 glowColor = ${bandRgbConst(tier, band)};`,
      "  float across = abs(materialInput.st.t - 0.5) * 2.0;", // 0 at centreline, 1 at the rim
      `  float a = fract(${phase} + across * 2.0);`, // sharp slant ⇒ a clean arrowhead silhouette
      "  float arrow = smoothstep(0.48, 0.60, a) * (1.0 - smoothstep(0.70, 0.82, a));", // bold ~22% band
      "  m.diffuse = glowColor;",
      `  m.emission = glowColor * (${emFloor.toFixed(2)} + 1.7 * arrow);`, // danger ⇒ the whole edge glows, not just the chevron
      `  m.alpha = ${aFloor.toFixed(2)} + ${(1 - aFloor).toFixed(2)} * arrow;`,
      "  return m;",
    ].join("\n") + "\n}",
  };
}

// Static (de-energized / OUT OF SERVICE) variant. P14: a de-energized cable reads DEAD — a desaturated
// GREY (#555555) at low alpha — REGARDLESS of its voltage tier, so a shutdown is unmistakable (it no
// longer just dims its own tier hue, which read as "still a bit live"). No `time` dependence ⇒ nothing
// animates. (User: "the shutdown edge itself is always displayed, but the moving-arrow current display
// is turned off.")
const SHUTDOWN_GREY = hexToVec3("#555555");
export function staticFabric(tier = "MV"): FlowFabric {
  return {
    type: `${FLOW_MATERIAL_TYPE}_${tier}_OFF`,
    uniforms: { time: 0 },
    source: [
      "czm_material czm_getMaterial(czm_materialInput materialInput) {",
      "  czm_material m = czm_getDefaultMaterial(materialInput);",
      `  m.diffuse = ${SHUTDOWN_GREY};`,
      "  m.emission = vec3(0.0);",
      "  m.alpha = 0.18;", // dead grey — present but unmistakably de-energized, tier hue gone
      "  return m;",
    ].join("\n") + "\n}",
  };
}

// In-service IDLE variant: the edge is a LIVE circuit (both ends energized, closed) but carries ~0 flow
// this tick — an idle parallel circuit or a switchyard busbar. It renders SOLID in its tier colour (a
// real, in-service line) with NO moving arrows, clearly distinct from the dead grey de-energized
// `staticFabric`. P14: an overloaded but idle leg still rides the danger band (`band` overrides the hue).
// User: light up in-service lines like the reference; arrows only where flow.
export function inServiceFabric(tier = "MV", band: CrisisBucket = "base"): FlowFabric {
  const idleEm = band === "base" ? 0.35 : bandGlow(band).emFloor; // a danger idle leg still glows its danger hue
  return {
    type: `${FLOW_MATERIAL_TYPE}_${tier}_IDLE_${band}`,
    uniforms: { time: 0 },
    source: [
      "czm_material czm_getMaterial(czm_materialInput materialInput) {",
      "  czm_material m = czm_getDefaultMaterial(materialInput);",
      `  const vec3 glowColor = ${bandRgbConst(tier, band)};`,
      "  m.diffuse = glowColor;",
      `  m.emission = glowColor * ${idleEm.toFixed(2)};`, // solid, in-service glow — no `time` ⇒ nothing animates
      `  m.alpha = ${band === "base" ? "0.85" : "0.92"};`, // solid in-service line (vs 0.18 dead de-energized)
      "  return m;",
    ].join("\n") + "\n}",
  };
}

// P14 edge-SELECTION variant: the one clicked cable lights up a bright solid CVD-safe yellow (#F0E442)
// at high alpha — the whole length recolours (paired with a width bump in the adapter), so the selected
// edge is unmistakable over any tier/band/state. One cached material, swapped onto exactly one polyline.
export const SELECTION_MATERIAL_TYPE = `${FLOW_MATERIAL_TYPE}_SEL`;
const SELECTION_RGB = hexToVec3("#F0E442");
export function selectionFabric(): FlowFabric {
  return {
    type: SELECTION_MATERIAL_TYPE,
    uniforms: { time: 0 },
    source: [
      "czm_material czm_getMaterial(czm_materialInput materialInput) {",
      "  czm_material m = czm_getDefaultMaterial(materialInput);",
      `  const vec3 selColor = ${SELECTION_RGB};`,
      "  m.diffuse = selColor;",
      "  m.emission = selColor * 0.9;", // bright, solid — the selection highlight reads at a glance
      "  m.alpha = 0.95;",
      "  return m;",
    ].join("\n") + "\n}",
  };
}

export function makeFlowMaterial(cesium: CesiumLike, tier = "MV", dir: 1 | -1 = 1, band: CrisisBucket = "base"): MaterialLike {
  return new cesium.Material({ fabric: flowFabric(0, tier, dir, band) });
}

export function makeInServiceMaterial(cesium: CesiumLike, tier = "MV", band: CrisisBucket = "base"): MaterialLike {
  return new cesium.Material({ fabric: inServiceFabric(tier, band) });
}

export function makeStaticMaterial(cesium: CesiumLike, tier = "MV"): MaterialLike {
  return new cesium.Material({ fabric: staticFabric(tier) });
}

export function makeSelectionMaterial(cesium: CesiumLike): MaterialLike {
  return new cesium.Material({ fabric: selectionFabric() });
}

export function setFlowTime(mat: MaterialLike, t: number): void {
  mat.uniforms.time = t; // mutate the uniform in place — no new material, no per-entity property
}
